import React from "react";
import { CheckCircle2, XCircle, ExternalLink, AlertCircle, FileDown } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

/**
 * Toont per zending of hij is aangemeld bij MyParcel (met barcode + track&trace),
 * of gefaald (met reden). Ook een samenvattings-header en optionele download-knop
 * voor de PDF met labels.
 *
 * Props:
 *  open, onOpenChange — Dialog state
 *  resultaat = { verzonden, fouten[], resultaten[{status,pakbon,organisatie,reden,barcode,tracktrace_url,carrier}], bericht, label_pdf_base64, bestandsnaam }
 *  onDownloadLabels() — callback om de PDF met labels te downloaden
 */
export function VerzendResultaatDialog({ open, onOpenChange, resultaat, onDownloadLabels }) {
  if (!resultaat) return null;
  const rs = resultaat.resultaten || [];
  const gelukt = rs.filter((r) => r.status === "gelukt");
  const mislukt = rs.filter((r) => r.status !== "gelukt");
  const totaal = rs.length || (resultaat.verzonden + (resultaat.fouten || []).length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl w-[95vw] max-h-[85vh] overflow-y-auto" data-testid="verzend-resultaat-dialog">
        <DialogHeader>
          <DialogTitle>MyParcel-aanmelding — resultaat</DialogTitle>
        </DialogHeader>

        {/* Samenvatting */}
        <div className="grid grid-cols-3 gap-3 pt-2">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs uppercase text-muted-foreground tracking-wide">Totaal</div>
            <div className="font-display text-2xl font-medium tabular-nums" data-testid="verzend-totaal">{totaal}</div>
          </div>
          <div className="rounded-lg border border-emerald-300/60 bg-emerald-50 p-3">
            <div className="text-xs uppercase text-emerald-700 tracking-wide">Gelukt</div>
            <div className="font-display text-2xl font-medium tabular-nums text-emerald-800" data-testid="verzend-gelukt">{gelukt.length}</div>
          </div>
          <div className={`rounded-lg border p-3 ${mislukt.length ? "border-amber-400/60 bg-amber-50" : "border-border"}`}>
            <div className={`text-xs uppercase tracking-wide ${mislukt.length ? "text-amber-800" : "text-muted-foreground"}`}>Mislukt</div>
            <div className={`font-display text-2xl font-medium tabular-nums ${mislukt.length ? "text-amber-900" : ""}`} data-testid="verzend-mislukt">{mislukt.length}</div>
          </div>
        </div>

        {/* Optionele info-bericht (bv. account op vooraf betalen) */}
        {resultaat.bericht && (
          <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-400/60 bg-amber-50 p-3 text-sm text-amber-900" data-testid="verzend-bericht">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>{resultaat.bericht}</div>
          </div>
        )}

        {/* Gelukte zendingen */}
        {gelukt.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase text-muted-foreground tracking-wide flex items-center gap-2">
              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> Aangemeld ({gelukt.length})
            </div>
            <div className="border border-border rounded-lg divide-y divide-border" data-testid="verzend-gelukt-lijst">
              {gelukt.map((r) => (
                <div key={r.id} className="p-2.5 flex items-start justify-between gap-3 text-sm">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{r.pakbon}</div>
                    <div className="text-xs text-muted-foreground truncate">{r.organisatie}</div>
                    {r.barcode && (
                      <div className="text-xs font-mono text-muted-foreground mt-0.5">
                        Barcode: {r.barcode}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.carrier && <Badge variant="outline" className="text-[10px]">{r.carrier}</Badge>}
                    {r.tracktrace_url && (
                      <a href={r.tracktrace_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
                        data-testid={`verzend-tt-${r.id}`}>
                        Track&Trace <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Mislukte zendingen — hier komt de "duidelijke aanwijzing" */}
        {mislukt.length > 0 && (
          <div className="mt-4 space-y-2">
            <div className="text-xs uppercase text-amber-900 tracking-wide flex items-center gap-2 font-medium">
              <XCircle className="h-3.5 w-3.5 text-amber-700" /> Niet aangemeld — actie nodig ({mislukt.length})
            </div>
            <div className="border border-amber-400/60 rounded-lg divide-y divide-amber-200 bg-amber-50/50" data-testid="verzend-mislukt-lijst">
              {mislukt.map((r) => (
                <div key={r.id} className="p-2.5 text-sm">
                  <div className="font-medium">{r.pakbon}</div>
                  <div className="text-xs text-muted-foreground">{r.organisatie}</div>
                  <div className="text-xs text-amber-900 mt-1">
                    <span className="font-medium">Reden:</span> {r.reden}
                  </div>
                  {r.reden && r.reden.toLowerCase().includes("adres") && (
                    <div className="text-[11px] text-amber-800 mt-1">
                      → Vul het bezorgadres aan op de organisatiekaart en probeer opnieuw.
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="pt-4 flex-col sm:flex-row gap-2">
          {resultaat.label_pdf_base64 && onDownloadLabels && (
            <Button variant="secondary" onClick={onDownloadLabels} data-testid="verzend-download-labels">
              <FileDown className="h-4 w-4 mr-1" /> A6-labels downloaden (origineel)
            </Button>
          )}
          <Button onClick={() => onOpenChange(false)} data-testid="verzend-resultaat-sluiten">Sluiten</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
